import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Collapse from '@mui/material/Collapse';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import WarningRoundedIcon from '@mui/icons-material/WarningRounded';
import { pureWhite, bentoSlate1 } from './colors';
import type { AppNavigationSubLink } from './AppLayout';

const selectedBtnColor = 'rgba(129, 203, 241, 0.3)';

const activeListItemStyle = {
  cursor: 'pointer',
  backgroundColor: selectedBtnColor,
  color: pureWhite,
  borderLeft: '4px solid #81cbf1',
  '&:hover': {
    borderLeft: '4px solid #81cbf1',
  },
};

const inactiveListItemStyle = {
  cursor: 'pointer',
  borderLeft: `4px solid ${bentoSlate1}`,
  '&:hover': {
    backgroundColor: selectedBtnColor,
    color: pureWhite,
    borderLeft: '4px solid rgba(0, 0, 0, 0)',
  },
};

interface NavMenuItemProps {
  id: string;
  title: string;
  route: string | string[];
  isActive: boolean;
  items?: AppNavigationSubLink[];
  showWarningFor?: boolean;
  onClick?: () => void;
}

function NavMenuItem({
  id,
  title,
  route,
  isActive,
  items = [],
  showWarningFor = false,
  onClick = () => {},
}: NavMenuItemProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const hasItems = items.length > 0;

  const navigateToRoute = () => {
    const newUrl = Array.isArray(route) ? route[0] : route;
    navigate(newUrl);
    onClick();
  };

  const handleClick = () => {
    navigateToRoute();
    if (hasItems && !open) {
      setOpen(true);
    }
  };

  const handleToggleCollapse = (event: React.MouseEvent) => {
    event.stopPropagation();
    setOpen(!open);
  };

  return (
    <>
      <ListItem sx={isActive ? activeListItemStyle : inactiveListItemStyle}>
        <ListItemText
          id={`nav-menu-${id}`}
          primary={title}
          slotProps={{
            primary: {
              fontFamily: 'inherit',
              fontWeight: 600,
            },
          }}
          onClick={handleClick}
        />
        {hasItems && (
          <ListItemIcon
            sx={{ minWidth: 32, cursor: 'pointer', color: 'inherit' }}
            onClick={handleToggleCollapse}
          >
            {open ? <ExpandLess /> : <ExpandMore />}
          </ListItemIcon>
        )}
      </ListItem>
      <Collapse in={open} timeout="auto" unmountOnExit>
        {items.map((item) => {
          const subItemActive = !isActive && location.pathname === item.route;
          const subListItemStyle = subItemActive ? activeListItemStyle : inactiveListItemStyle;
          return (
            <ListItem
              key={item.title}
              sx={{ ...subListItemStyle, paddingTop: 1, paddingBottom: 1, paddingLeft: 3 }}
              onClick={() => navigate(item.route)}
            >
              {item.showWarning && showWarningFor && (
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <WarningRoundedIcon color="error" />
                </ListItemIcon>
              )}
              <ListItemText
                primary={item.title}
                slotProps={{
                  primary: {
                    fontFamily: 'inherit',
                    fontWeight: 400,
                  },
                }}
              />
            </ListItem>
          );
        })}
      </Collapse>
    </>
  );
}

export default NavMenuItem;
